import { ActionIcon, Group, Menu, Popover, TextInput } from '@mantine/core';
import {
  IconFileText,
  IconMoodSmile,
  IconPaperclip,
  IconPhoto,
  IconSend,
} from '@tabler/icons';
import type { EmojiClickData } from 'emoji-picker-react';
import React, { useCallback, useRef, useState } from 'react';
import { EmojiPickerWrapper } from './EmojiPickerWrapper';

interface ChatInputProps {
  onSend: (content: string) => Promise<void>;
  onSendAttachment: (file: File) => Promise<void>;
}

// Stable style for the hidden file inputs — not recreated on render.
const hiddenInputStyle: React.CSSProperties = { display: 'none' };

export const ChatInput = React.memo(function ChatInput({
  onSend,
  onSendAttachment,
}: ChatInputProps) {
  const [value, setValue] = useState('');
  const [uploading, setUploading] = useState(false);
  const [emojiOpened, setEmojiOpened] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);

  const handleEmojiClick = useCallback((emojiData: EmojiClickData) => {
    setValue((prev) => prev + emojiData.emoji);
  }, []);

  const handleSend = useCallback(async () => {
    if (!value.trim()) return;
    const content = value;
    setValue('');
    await onSend(content);
  }, [value, onSend]);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = '';
      setUploading(true);
      try {
        await onSendAttachment(file);
      } finally {
        setUploading(false);
      }
    },
    [onSendAttachment],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') handleSend();
    },
    [handleSend],
  );

  const handleToggleEmoji = useCallback(() => setEmojiOpened((o) => !o), []);
  const handleCloseEmoji = useCallback(() => setEmojiOpened(false), []);
  const handleClickImage = useCallback(
    () => imageInputRef.current?.click(),
    [],
  );
  const handleClickDoc = useCallback(() => docInputRef.current?.click(), []);

  return (
    <Group spacing="xs">
      {/* Hidden file inputs */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        style={hiddenInputStyle}
        onChange={handleFileChange}
      />
      <input
        ref={docInputRef}
        type="file"
        accept="application/pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
        style={hiddenInputStyle}
        onChange={handleFileChange}
      />

      {/* Attachment menu */}
      <Menu position="top-start" withArrow shadow="md">
        <Menu.Target>
          <ActionIcon
            size="md"
            variant="light"
            loading={uploading}
            title="Enviar arquivo"
          >
            <IconPaperclip size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item icon={<IconPhoto size={14} />} onClick={handleClickImage}>
            Imagem
          </Menu.Item>
          <Menu.Item icon={<IconFileText size={14} />} onClick={handleClickDoc}>
            Documento
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      {/* Text input with emoji picker */}
      <TextInput
        placeholder="Mensagem..."
        sx={{ flex: 1 }}
        value={value}
        onChange={(e) => setValue(e.currentTarget.value)}
        onKeyDown={handleKeyDown}
        size="sm"
        autoComplete="off"
        autoSave="off"
        rightSectionWidth={45}
        rightSection={
          <Popover
            opened={emojiOpened}
            onClose={handleCloseEmoji}
            position="top-end"
            withArrow
            shadow="md"
            zIndex={3000}
          >
            <Popover.Target>
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={handleToggleEmoji}
                size="sm"
              >
                <IconMoodSmile size={20} />
              </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown p={0}>
              {/* Only mount the heavy picker when it's actually open */}
              {emojiOpened && (
                <EmojiPickerWrapper onEmojiClick={handleEmojiClick} />
              )}
            </Popover.Dropdown>
          </Popover>
        }
      />

      <ActionIcon
        variant="filled"
        color="blue"
        onClick={handleSend}
        disabled={!value.trim()}
        radius="xl"
        size="lg"
      >
        <IconSend size={18} />
      </ActionIcon>
    </Group>
  );
});
